export type ConsentChoice = 'granted' | 'denied'

export type ConsentState = {
  analytics: ConsentChoice
  ads: ConsentChoice
  personalization: ConsentChoice
  functionality: ConsentChoice
  security: ConsentChoice
}

const STORAGE_KEY = 'consent_status'
const LEGACY_STORAGE_KEY = 'nxt-consent'

const US_TIME_ZONES = new Set([
  'America/New_York',
  'America/Detroit',
  'America/Kentucky/Louisville',
  'America/Kentucky/Monticello',
  'America/Indiana/Indianapolis',
  'America/Indiana/Vincennes',
  'America/Indiana/Winamac',
  'America/Indiana/Marengo',
  'America/Indiana/Petersburg',
  'America/Indiana/Vevay',
  'America/Chicago',
  'America/Menominee',
  'America/North_Dakota/Center',
  'America/North_Dakota/New_Salem',
  'America/North_Dakota/Beulah',
  'America/Denver',
  'America/Boise',
  'America/Phoenix',
  'America/Los_Angeles',
  'America/Anchorage',
  'America/Juneau',
  'America/Sitka',
  'America/Metlakatla',
  'America/Yakutat',
  'America/Nome',
  'America/Adak',
  'Pacific/Honolulu',
])

type RegionSignals = {
  timeZone?: string
  languageTags?: string[]
}

const LOCALE_REGION_SEGMENT = /^[A-Za-z]{2}$/

function getLocalStorage(): Storage | null {
  if (typeof window === 'undefined') {
    return null
  }

  try {
    return window.localStorage
  } catch {
    return null
  }
}

export function shouldLoadGtmForConsent(state: ConsentState): boolean {
  return Object.values(state).some((choice) => choice === 'granted')
}

function parseLocaleRegion(localeTag: string): string | null {
  const normalized = localeTag.trim().replace(/_/g, '-')
  if (!normalized) {
    return null
  }

  // BCP-47 region is not the first segment (the first is language).
  const [, ...segments] = normalized.split('-')
  for (const segment of segments) {
    if (LOCALE_REGION_SEGMENT.test(segment)) {
      return segment.toUpperCase()
    }
  }

  return null
}

export function isLikelyUsUser({ timeZone, languageTags = [] }: RegionSignals): boolean {
  if (timeZone && US_TIME_ZONES.has(timeZone)) {
    return true
  }

  return languageTags.some((tag) => parseLocaleRegion(tag) === 'US')
}

function getRegionSignals(): RegionSignals {
  const timeZone = typeof Intl !== 'undefined'
    ? Intl.DateTimeFormat().resolvedOptions().timeZone
    : undefined

  if (typeof navigator === 'undefined') {
    return { timeZone, languageTags: [] }
  }

  const languageTags = [
    ...(navigator.languages ?? []),
    navigator.language,
  ].filter((value): value is string => Boolean(value))

  return { timeZone, languageTags }
}

export function shouldRequireConsentPrompt(): boolean {
  return !isLikelyUsUser(getRegionSignals())
}

function normalizeConsentState(state: Partial<ConsentState> & {
  analytics?: ConsentChoice
  ads?: ConsentChoice
}): ConsentState | null {
  if (
    !state.analytics ||
    !state.ads ||
    (state.analytics !== 'granted' && state.analytics !== 'denied') ||
    (state.ads !== 'granted' && state.ads !== 'denied')
  ) {
    return null
  }

  const fallback = state.analytics
  return {
    analytics: state.analytics,
    ads: state.ads,
    personalization: state.personalization ?? fallback,
    functionality: state.functionality ?? fallback,
    security: state.security ?? fallback,
  }
}

export function getStoredConsent(): ConsentState | null {
  const storage = getLocalStorage()
  if (!storage) {
    return null
  }

  const raw = storage.getItem(STORAGE_KEY) ?? storage.getItem(LEGACY_STORAGE_KEY)
  if (!raw) {
    return null
  }

  try {
    const parsed = JSON.parse(raw) as ConsentState
    return normalizeConsentState(parsed)
  } catch {
    return null
  }
}

export function storeConsent(state: ConsentState) {
  const storage = getLocalStorage()
  if (!storage) {
    return
  }

  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch {
    // Best-effort persistence only.
  }
}

export function pushConsentUpdate(state: ConsentState) {
  if (typeof window === 'undefined') {
    return
  }

  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({
    event: 'consent_update',
    analytics_storage: state.analytics,
    ad_storage: state.ads,
    ad_user_data: state.ads,
    ad_personalization: state.ads,
    personalization_storage: state.personalization,
    functionality_storage: state.functionality,
    security_storage: state.security,
  })
}
